import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { VerifiedUser, Download, Person, PersonOutline } from '@mui/icons-material';
import proceedingService from '../services/proceedingService';

const DocumentSignatures = ({ open, onClose, proceedingId, documentId, documentName }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && proceedingId && documentId) {
      loadSignatures();
    }
  }, [open, proceedingId, documentId]);

  const loadSignatures = async () => {
    setLoading(true);
    try {
      const res = await proceedingService.getDocumentSignatures(proceedingId, documentId);
      setSignatures(res.data || []);
    } catch {
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    proceedingService.downloadSignatureReport(proceedingId, documentId);
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VerifiedUser color="success" />
        Firmas — {documentName}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : signatures.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No hay firmas para este documento.
          </Typography>
        ) : (
          signatures.map((sig, idx) => (
            <Box key={sig.id?.toString() || idx}>
              {idx > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {sig.signerType === 'internal' ? (
                      <Person fontSize="small" color="primary" />
                    ) : (
                      <PersonOutline fontSize="small" color="secondary" />
                    )}
                    <Typography fontWeight={600}>{sig.signerName}</Typography>
                    <Chip
                      label={sig.signerType === 'internal' ? 'Interno' : 'Externo'}
                      size="small"
                      color={sig.signerType === 'internal' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {sig.signerEmail}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(sig.createdAt)} · Token: {sig.signatureToken}
                  </Typography>
                </Box>
                {sig.signatureImageUrl && (
                  <Box
                    sx={{
                      width: 120,
                      height: 60,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#fafafa',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={sig.signatureImageUrl}
                      alt="Firma"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        {signatures.length > 0 && (
          <Button startIcon={<Download />} onClick={handleDownloadReport} variant="outlined" size="small">
            Descargar reporte PDF
          </Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentSignatures;
